import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import CustomCheckbox from "../CustomCheckbox";
import CustomButton from "../CustomButton";
import CustomInput from "../CustomInput";
import Dropdown from "../Dropdown";
import CustomInputWithUnit from "../CustomInputWithUnit";

export const TABLE_CELL_TYPES_LISTED = Object.freeze({
  LINK: "link",
  TEXT: "text",
  DATE: "date",
  MONEY: "money",
  NOTES: "notes",
  ELEMENT: "element",
  REQUEST: "request",
});

const ListedTable = ({
  columns = [],
  withCheckbox = false,
  selectedRows = [],
  setSelectedRows = () => {},
  fullData = [],
  initialData = [],
  handleAddRow,
  onFetchProductName = () => {},
  loadingProductName,
  handleSelectCurrency = {},
  selectedCurrency,
}) => {
  const { t } = useTranslation("general");

  const currencies = ["IQD", "USD"];

  // Create initialRow based on dynamic columns
  const createInitialRow = () => {
    const row = {};
    columns.forEach((column) => {
      row[column.key] = "";
    });
    return row;
  };

  const initialRow = createInitialRow();

  const [rows, setRows] = useState(
    initialData.length > 0 ? initialData : [initialRow]
  );

  const inputRefs = useRef([]);
  const [selectAll, setSelectAll] = useState(false);

  // Initialize inputRefs for each cell
  useEffect(() => {
    inputRefs.current = rows.map(() =>
      Array(columns.length)
        .fill()
        .map(() => React.createRef())
    );
  }, [rows.length, columns.length]);

  useEffect(() => {
    const allSelected =
      rows.length > 0 &&
      rows.every((_, index) => selectedRows.some((item) => item.id === index));
    setSelectAll(allSelected);
  }, [selectedRows, rows]);

  useEffect(() => {
    if (initialData.length === 0) {
      setRows([createInitialRow()]);
    } else {
      setRows(initialData);
    }
  }, [initialData]);

  useEffect(() => {
    handleAddRow(rows);
  }, [rows.length]);

  const handleChange = (e, rowIndex, field) => {
    const newRows = [...rows];
    newRows[rowIndex][field] = e.target.value;

    // Auto-calculate totalAmount if price or amount changes
    if (field === "quantity" || field === "price") {
      const price = parseFloat(newRows[rowIndex].price || 0);
      const quantity = parseFloat(newRows[rowIndex].quantity || 0);
      newRows[rowIndex].totalAmount = (price * quantity).toFixed(2);
    }
    setRows(newRows);
    handleAddRow(newRows);
    console.log("NEWROWS", newRows);
  };

  const handleBlur = (e, rowIndex, field) => {
    if (field === "barcode" && e.target.value.trim() !== "") {
      onFetchProductName(e.target.value.trim(), rowIndex, rows, setRows);
    }
  };

  const handleKeyDown = (e, rowIndex, colIndex) => {
    if (e.key === "Enter") {
      e.preventDefault();

      // if (
      //   columns[colIndex].key === "barcode" &&
      //   rows[rowIndex].barcode.trim() !== ""
      // ) {
      //   onFetchProductName(
      //     rows[rowIndex].barcode.trim(),
      //     rowIndex,
      //     rows,
      //     setRows
      //   );
      // }

      const isPriceField = e?.target?.name === "price";

      if (isPriceField) {
        setRows([...rows, initialRow]);
        if (
          inputRefs.current[rowIndex + 1] &&
          inputRefs.current[rowIndex + 1][0]
        ) {
          inputRefs.current[rowIndex + 1][0].current?.focus();
        }

        console.log("inputRefs.current[nextRow]", inputRefs.current[rowIndex]);
      } else {
        const nextCol = colIndex === columns.length - 1 ? 0 : colIndex + 1;
        console.log("nextCol", nextCol);
        const nextRow =
          colIndex === columns.length - 1 ? rowIndex + 1 : rowIndex;

        console.log("nextRow", nextRow);
        // console.log("inputRefs.current[nextRow][nextCol]", nextRow][nextCol]);
        if (inputRefs.current[nextRow] && inputRefs.current[nextRow][nextCol]) {
          inputRefs.current[nextRow][nextCol].current?.focus();
        }
      }
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedRows([]);
    } else {
      const allRows = rows.map((row, index) => ({
        id: index,
        data: row,
      }));
      setSelectedRows(allRows);
    }
    setSelectAll(!selectAll);
  };

  const handleSelectOne = (id) => {
    const rowData = { id, data: rows.find((_, index) => index === id) };

    setSelectedRows((prevSelected) => {
      const isSelected = prevSelected.some((item) => item.id === id);

      if (isSelected) {
        return prevSelected.filter((item) => item.id !== id);
      } else {
        return [...prevSelected, rowData];
      }
    });
  };

  const handleAddNewRow = () => {
    setRows((prevRows) => [...prevRows, createInitialRow()]);
  };

  return (
    <>
      <section className="rounded-table-container">
        <table className="i-table">
          <thead className="table-head">
            <tr>
              {withCheckbox && (
                <th className="bold-text">
                  <CustomCheckbox
                    checked={selectAll}
                    onChange={handleSelectAll}
                    color="primary"
                  />
                </th>
              )}
              {columns.map((column, index) => (
                <th
                  key={index}
                  className={`cell-title ${index === 0 ? "bold-text" : ""} ${
                    column.keepOnMobile ? "" : "mobile-hider"
                  }`}
                >
                  {t(column.title)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="table-body">
            {rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {withCheckbox && (
                  <td className="cell the-rounded-first-cell">
                    <CustomCheckbox
                      checked={selectedRows.some(
                        (item) => item.id === rowIndex
                      )}
                      onChange={() => handleSelectOne(rowIndex)}
                    />
                  </td>
                )}
                {columns.map((column, colIndex) => {
                  const isFirstCell = !withCheckbox && colIndex === 0;
                  const isLastCell = colIndex === columns.length - 1;
                  const cellClasses = [
                    "cell",
                    isFirstCell ? "the-rounded-first-cell" : "",
                    isLastCell ? "the-rounded-last-cell" : "",
                    column.keepOnMobile ? "" : "mobile-hider",
                  ]
                    .filter(Boolean)
                    .join(" ");

                  return (
                    <td key={column.key} className={cellClasses}>
                      {column.key === "currency" ? (
                        <CustomInput
                          // value={selectedCurrency}
                          type="text"
                          value={selectedCurrency?.value || ""}
                          onChange={(e) => {
                            handleSelectCurrency(e?.target?.value);
                          }}
                          // thick
                          flex
                        />
                      ) : column.key === "totalAmount" ? (
                        <CustomInputWithUnit
                          value={row[column.key] || ""}
                          onChange={(e) =>
                            handleChange(e, rowIndex, column.key)
                          }
                          onBlur={(e) => handleBlur(e, rowIndex, column.key)}
                          onKeyDown={(e) =>
                            handleKeyDown(e, rowIndex, colIndex)
                          }
                          ref={
                            inputRefs.current[rowIndex]
                              ? inputRefs.current[rowIndex][colIndex]
                              : null
                          }
                          type="text"
                          unit={selectedCurrency?.value}
                          readonly={true}
                        />
                      ) : column.key === "quantity" ? (
                        <div className="o-row o-row--vertical-align_center o-row--gap-size_100">
                          <CustomInput
                            value={row[column.key] || ""}
                            onChange={(e) =>
                              handleChange(e, rowIndex, column.key)
                            }
                            onBlur={(e) => handleBlur(e, rowIndex, column.key)}
                            onKeyDown={(e) =>
                              handleKeyDown(e, rowIndex, colIndex)
                            }
                            ref={
                              inputRefs.current[rowIndex]
                                ? inputRefs.current[rowIndex][colIndex]
                                : null
                            }
                            type="text"
                          />
                          <span>{row?.unitOfMeasure?.toLowerCase()}</span>
                        </div>
                      ) : column.key === "price" ? (
                        <div className="o-row o-row--vertical-align_center o-row--gap-size_100">
                          <CustomInput
                            value={row[column.key] || ""}
                            onChange={(e) =>
                              handleChange(e, rowIndex, column.key)
                            }
                            onBlur={(e) => handleBlur(e, rowIndex, column.key)}
                            onKeyDown={(e) =>
                              handleKeyDown(e, rowIndex, colIndex)
                            }
                            ref={
                              inputRefs.current[rowIndex]
                                ? inputRefs.current[rowIndex][colIndex]
                                : null
                            }
                            type="text"
                            name="price"
                          />
                          <span>{selectedCurrency?.value}</span>
                        </div>
                      ) : (
                        <CustomInput
                          value={row[column.key] || ""}
                          onChange={(e) =>
                            handleChange(e, rowIndex, column.key)
                          }
                          onBlur={(e) => handleBlur(e, rowIndex, column.key)}
                          onKeyDown={(e) =>
                            handleKeyDown(e, rowIndex, colIndex)
                          }
                          ref={
                            inputRefs.current[rowIndex]
                              ? inputRefs.current[rowIndex][colIndex]
                              : null
                          }
                          type="text"
                          loading={loadingProductName}
                        />
                      )}
                      {/* <input
                        value={row[column.key] || ""}
                        onChange={(e) => handleChange(e, rowIndex, column.key)}
                        onBlur={(e) => handleBlur(e, rowIndex, column.key)}
                        onKeyDown={(e) => handleKeyDown(e, rowIndex, colIndex)}
                        ref={
                          inputRefs.current[rowIndex]
                            ? inputRefs.current[rowIndex][colIndex]
                            : null
                        }
                        type="text"
                        className="w-full px-2 py-1 border-none bg-transparent focus:outline-none"
                      /> */}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </section>
      <div className="o-row o-row--horizontal-align_end u-margin--top_300">
        <CustomButton title={t("addAnotherItem")} onClick={handleAddNewRow} />
      </div>
    </>
  );
};

export default ListedTable;
